import { useMemo } from 'react';

import { AddIcon, Button, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useStableCallback } from '~/hooks/common';
import { useUserInfoStore } from '~/store';
import { getClaimExchangeColumnDefs } from '~/utils/claim/table';

import { CLAIM_PAGE_SIZE } from '~/constants/claim';

import type { TypeExchangeData } from '~/types/claim.types';
import type { TypePagination } from '~/types/common.types';

import styles from './ClaimExchangeTable.module.scss';

const ClaimExchangeTable = ({
  data,
  pagination,
  selectedItem,
  onChangePageLimit,
  onChangePage,
  onChangeRowSelection,
  onClickCancelExchange,
  onClickResendExchange,
  onClickRegisterExchange,
  onClickExchangeReceiptId,
  onClickExchangeOrderCode,
}: {
  data: TypeExchangeData[] | null;
  pagination: TypePagination;
  selectedItem: TypeExchangeData | null;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: TypeExchangeData[]) => void;
  onClickCancelExchange: () => void;
  onClickResendExchange: () => void;
  onClickRegisterExchange: () => void;
  onClickExchangeReceiptId: (exchangeReceiptId: string) => void;
  onClickExchangeOrderCode: (outboundOrderCode: string) => void;
}) => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);

  const handleClickExchangeReceiptId = useStableCallback(onClickExchangeReceiptId);
  const handleClickExchangeOrderCode = useStableCallback(onClickExchangeOrderCode);
  const { columnDefs } = useMemo(
    () =>
      getClaimExchangeColumnDefs({
        onClickExchangeReceiptId: handleClickExchangeReceiptId,
        onClickExchangeOrderCode: handleClickExchangeOrderCode,
      }),
    [handleClickExchangeReceiptId, handleClickExchangeOrderCode]
  );

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            {hasPermission3PL && (
              <Button
                size="tiny"
                variant="outlined"
                status="error"
                disabled={!selectedItem}
                onClick={onClickCancelExchange}
              >
                교환 취소
              </Button>
            )}
            <Button size="tiny" color="secondary" disabled={!selectedItem} onClick={onClickResendExchange}>
              교환 재배송 처리
            </Button>
            {hasPermission3PL && (
              <Button size="tiny" color="secondary" leftIcon={<AddIcon size={16} />} onClick={onClickRegisterExchange}>
                교환 신청
              </Button>
            )}
          </div>
        }
      />
      <div className={styles['table-wrapper']}>
        <Table
          data={data}
          columns={columnDefs}
          rowStatusType={data === null ? 'INIT' : 'EMPTY'}
          selection={{ mode: 'single', onChange: onChangeRowSelection }}
        />
      </div>
      <TableBottomField
        pageSizeOptions={Object.values(CLAIM_PAGE_SIZE)}
        currentPage={pagination.currentPage}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={onChangePage}
      />
    </div>
  );
};

export default ClaimExchangeTable;
